import {useState, useMemo, useCallback, useEffect} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";

import {getFilterConfig} from "../api/customizations";
import {addOptionsToFilters} from "../context/helpers";
import {useKnowledgeData} from "./useKnowledgeData";
import {useFields} from "./useFields";
import {useReleases} from "./useReleases";
import {buildReleaseOptions} from "../utils/releaseOptions";
import {FilterConfig } from "../components/common/filters/types";

interface IdeaFilters {
  evidence: any;
  idea: any;
}

interface UseIdeaFiltersProps {
  isEvidencePage?: boolean;
}

interface UseIdeaFiltersReturn {
  ideasFiltersConfig: IdeaFilters;
  setIdeasFiltersConfig: (config: any) => void;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  // Cache management
  clearCache: () => void;
  invalidateFilters: () => void;
  getCacheKey: () => readonly any[];
}

// Default filter configurations that will always be available
const DEFAULT_EVIDENCE_FILTERS: Record<string, FilterConfig> = {
  owner: {
    name: 'owner',
    label: 'Owner',
    type: 'checkbox',
    filterObject: 'evidence',
    dbFieldPath: 'ownerId',
    optionsKeyAppContext: 'owners',
    selectedIsObjId: true,
    generatedEnabled: true,
    enabled: true,
    showInInbox: true,
    displayIndex: 0,
    options: [],
  },
  'product-area': {
    name: 'product-area',
    label: 'Product Tree',
    type: 'checkbox',
    filterObject: 'evidence',
    dbFieldPath: 'componentId',
    optionsKeyAppContext: 'components',
    selectedIsObjId: true,
    generatedEnabled: true,
    enabled: true,
    showInInbox: true,
    displayIndex: 1,
    options: [],
  },
};

const DEFAULT_DB_FIELD_PATHS = new Set(
  Object.values(DEFAULT_EVIDENCE_FILTERS).map(f => f.dbFieldPath).filter(Boolean)
);

function deduplicateEvidenceFiltersByDbFieldPath(
  apiFilters: Record<string, FilterConfig>,
  defaults: Record<string, FilterConfig>,
): Record<string, FilterConfig> {
  const filteredApi: Record<string, FilterConfig> = {};
  const defaultKeys = new Set(Object.keys(defaults));

  Object.entries(apiFilters).forEach(([key, filter]) => {
    if (defaultKeys.has(key)) return;
    if (filter.dbFieldPath && DEFAULT_DB_FIELD_PATHS.has(filter.dbFieldPath)) return;
    filteredApi[key] = filter;
  });

  const clonedDefaults = Object.fromEntries(
    Object.entries(defaults).map(([k, v]) => [k, { ...v }])
  );

  return { ...filteredApi, ...clonedDefaults };
}

// Query keys factory - following the same pattern as ideasKeys in useIdeas
const ideaFiltersKeys = {
  all: ["ideasFiltersConfig"] as const,
  config: () => [...ideaFiltersKeys.all, "config"] as const,
  // Helper to invalidate all filter config data
  invalidateAll: () => [...ideaFiltersKeys.all] as const,
};

export const useIdeaFilters = ({
  isEvidencePage = false }:
   UseIdeaFiltersProps = {}): UseIdeaFiltersReturn => {
    
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const { components } = useFields();
  const {
    tagsOptions,
    members,
    owners,
    devTeams
  } = useKnowledgeData();
  const { releases: releaseList, groups: releaseGroups } = useReleases();

  /**
   * Options for the ideas-list release filter, registered by the backend as
   * `customizations` data with `optionsKeyAppContext: 'release'`.
   *
   * Both filter stacks render it with no new component — `type: 'checkbox'`
   * already maps to `CheckboxFilter` — but a checkbox filter still needs an
   * option list, and the stored values are ObjectId strings. Supplying it from
   * app context is exactly how the tag filters work; the `release` branch in
   * `addOptionsToFilters` reads this key.
   *
   * Each option carries its release group so `CheckboxFilter`'s `release`
   * branch can re-nest the flat list into the group→release tree.
   */
  const releaseOptions = useMemo(
    () => buildReleaseOptions(releaseList, releaseGroups),
    [releaseList, releaseGroups]
  );

  const filterConfigQueryKey = useMemo(() => {
    return ideaFiltersKeys.config();
  }, []);

  const baseUseQueryConfig = {
    enabled: !!auth0.isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false
  };

  // Filter config query
  const {
    data,
    isLoading: loading,
    error,
    refetch
  } = useQuery({
    ...baseUseQueryConfig,
    queryKey: filterConfigQueryKey,
    queryFn: async () => {
      const payload = await getFilterConfig(auth0);
      if (payload == null) return payload;
      // getFilterConfig already returns axios `response.data`. Some APIs wrap filters in `data`.
      const inner = (payload as { data?: IdeaFilters }).data;
      if (inner && (inner.idea !== undefined || inner.evidence !== undefined)) {
        return inner;
      }
      return payload as IdeaFilters;
    },
  });

  // Compute the base filters config by combining API data with context options
  const computedConfig = useMemo(() => {
    // If we don't have data yet, return empty config
    if (!data || (!data.idea && !data.evidence)) {
      return { idea: {}, evidence: {} };
    }

    const mergedEvidence = isEvidencePage
      ? deduplicateEvidenceFiltersByDbFieldPath(data.evidence || {}, DEFAULT_EVIDENCE_FILTERS)
      : (data.evidence || {});

    const merged = {
      ...data,
      evidence: mergedEvidence,
    };

    const updatedData = addOptionsToFilters(merged, {
      members,
      owners,
      components,
      tagsOptions,
      devTeams,
      releases: releaseOptions
    });

    // Add "Unassigned" option to owner filter if it exists
    if (updatedData.evidence?.owner && updatedData.evidence.owner.options) {
      const ownerOptions = Array.isArray(updatedData.evidence.owner.options)
        ? updatedData.evidence.owner.options
        : Object.entries(updatedData.evidence.owner.options).map(([value, label]) => ({
          value,
          label,
        }));

      // Check if "Unassigned" option already exists
      const hasUnassigned = ownerOptions.some((opt: any) =>
        (typeof opt === 'string' ? opt : opt.value) === 'unassigned'
      );

      if (!hasUnassigned) {
        // Add "Unassigned" option at the beginning
        const unassignedOption = {
          value: 'unassigned',
          label: 'Unassigned',
          _id: 'unassigned',
        };
        updatedData.evidence.owner.options = [unassignedOption, ...ownerOptions];
      }
    }

    return updatedData;
  }, [data, members, owners, components, tagsOptions, devTeams, releaseOptions, isEvidencePage]);

  // State to store the final config (allows manual updates via setIdeasFiltersConfig)
  const [ideasFiltersConfig, setIdeasFiltersConfigState] = useState<IdeaFilters>(computedConfig);

  // Update state when computed config changes
  useEffect(() => {
    // Only update if computed config has actual data
    if (computedConfig && 
        (computedConfig.idea && Object.keys(computedConfig.idea).length > 0 || 
         computedConfig.evidence && Object.keys(computedConfig.evidence).length > 0)) {
      setIdeasFiltersConfigState(computedConfig);
    }
  }, [computedConfig]);

  // setIdeasFiltersConfig allows manual updates (used by EvidencesHeader for quarters/weeks)
  const setIdeasFiltersConfig = useCallback((config: any) => {
    setIdeasFiltersConfigState(config);
  }, []);

  // Cache management callbacks
  const clearCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: ideaFiltersKeys.all });
    console.log('🗑️ Idea filters cache cleared');
  }, [queryClient]);

  const invalidateFilters = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ideaFiltersKeys.invalidateAll() });
    console.log('🔄 Idea filters cache invalidated for filter changes');
  }, [queryClient]);

  const getCacheKey = useCallback(() => {
    return filterConfigQueryKey;
  }, [filterConfigQueryKey]);

  return {
    ideasFiltersConfig,
    setIdeasFiltersConfig,
    loading,
    error: error as Error | null,
    refetch,
    // Cache management
    clearCache,
    invalidateFilters,
    getCacheKey,
  };
}; 